import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link, useNavigate} from '@tanstack/react-router'
import {useState} from "react";
import {stashGeneratorTournament} from "../start/generatorHandoff.ts";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/group-and-knockout-generator')({
    component: RouteComponent,
})

type GroupTeam = {name: string; qualifier: boolean};
type Group = {letter: string; teams: GroupTeam[]; fixtures: string[][]};
type KoMatch = {t1: string; t2: string; placeholder: boolean};
type KoRound = {title: string; matches: KoMatch[]};

function roundRobinFixtures(groupTeams: string[]): string[][] {
    let teams = [...groupTeams];
    if (teams.length % 2 !== 0) teams.push("BYE");

    const rounds: string[][] = [];
    const totalRounds = teams.length - 1;
    const matchesPerRound = teams.length / 2;

    for (let round = 0; round < totalRounds; round++) {
        const matches: string[] = [];
        for (let m = 0; m < matchesPerRound; m++) {
            const home = teams[m];
            const away = teams[teams.length - 1 - m];
            if (home !== "BYE" && away !== "BYE") matches.push(`${home} vs ${away}`);
        }
        rounds.push(matches);
        teams = [teams[0], teams[teams.length - 1], ...teams.slice(1, teams.length - 1)];
    }
    return rounds;
}

function nextPowerOfTwo(n: number): number {
    return Math.pow(2, Math.ceil(Math.log2(n)));
}

function koRoundName(size: number): string {
    if (size === 2) return "Final";
    if (size === 4) return "Semi-finals";
    if (size === 8) return "Quarter-finals";
    return `Round of ${size}`;
}

function buildGroupKnockout(teams: string[], groupCount: number, qualifiersPerGroup: number): {groups: Group[]; knockout: KoRound[]} {
    // Distribute teams across groups round-robin style.
    const buckets: string[][] = Array.from({length: groupCount}, () => []);
    teams.forEach((team, index) => buckets[index % groupCount].push(team));

    const groups: Group[] = buckets.map((groupTeams, i) => ({
        letter: String.fromCharCode(65 + i),
        teams: groupTeams.map((name, idx) => ({name, qualifier: idx < qualifiersPerGroup})),
        fixtures: roundRobinFixtures(groupTeams),
    }));

    // Seed qualifiers into a knockout bracket.
    const totalQualifiers = groupCount * qualifiersPerGroup;
    const bracketSize = nextPowerOfTwo(totalQualifiers);
    const qualifiers: string[] = [];
    groups.forEach((g) => {
        for (let q = 1; q <= qualifiersPerGroup; q++) qualifiers.push(`Group ${g.letter} #${q}`);
    });
    while (qualifiers.length < bracketSize) qualifiers.push("BYE");

    const knockout: KoRound[] = [];
    let roundSize = bracketSize;
    let roundNumber = 1;
    while (roundSize >= 2) {
        const matches: KoMatch[] = [];
        const count = roundSize / 2;
        for (let i = 0; i < count; i++) {
            if (roundNumber === 1) {
                matches.push({t1: qualifiers[i * 2], t2: qualifiers[i * 2 + 1], placeholder: false});
            } else {
                matches.push({t1: "Winner of previous round", t2: "Winner of previous round", placeholder: true});
            }
        }
        knockout.push({title: koRoundName(roundSize), matches});
        roundSize /= 2;
        roundNumber++;
    }

    return {groups, knockout};
}

const STEPS = [
    {title: "Create Groups", description: "Split participants into balanced groups for the opening stage."},
    {title: "Play Group Fixtures", description: "Each group plays round robin fixtures to determine standings."},
    {title: "Advance to Knockout", description: "Top teams or players qualify into a knockout bracket."},
];

const VS_ROWS = [
    {feature: "Multiple matches guaranteed", g: "Yes", rr: "Yes"},
    {feature: "Includes final bracket", g: "Yes", rr: "No"},
    {feature: "Best for large events", g: "Yes", rr: "No"},
    {feature: "Best for fair rankings", g: "Good", rr: "Excellent"},
    {feature: "Exciting final stage", g: "Yes", rr: "Sometimes"},
];

const BEST_FOR = ["Football, rugby and cricket tournaments", "Basketball competitions", "Padel and tennis club championships", "Pickleball and esports events", "School and corporate competitions", "Multi-stage club championships"];
const LESS_IDEAL = ["Very short one-hour events", "Simple social club nights", "Small groups with only four players", "Events where everyone must play everyone", "Ongoing ladder-style competitions"];

const FAQS = [
    {
        q: 'What is a group and knockout tournament?',
        a: <>It is a format where participants first play in groups, then the best performers qualify for a knockout bracket until a champion is decided.</>,
    },
    {
        q: 'How many teams should qualify from each group?',
        a: <>Most tournaments qualify one or two teams from each group. Two qualifiers per group usually gives more players a chance to reach the knockout stage.</>,
    },
    {
        q: 'How are group fixtures generated?',
        a: <>Group fixtures are generated as <Link to={"/round-robin-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">round robin</Link> schedules, where each participant plays the others in their group.</>,
    },
    {
        q: 'Is this format good for padel or pickleball?',
        a: <>Yes. It works well for padel, pickleball and tennis events where organizers want guaranteed matches before knockout rounds.</>,
    },
    {
        q: 'Can ScorePal manage group standings and knockout brackets?',
        a: <>Yes. ScorePal manages fixtures, live scores, standings, qualifiers, knockout brackets and player communication.</>,
    },
];

function RouteComponent() {
    const navigate = useNavigate();
    const [teamsText, setTeamsText] = useState("");
    const [groups, setGroups] = useState("2");
    const [qualifiers, setQualifiers] = useState("2");
    const [result, setResult] = useState<{groups: Group[]; knockout: KoRound[]} | null>(null);
    const [summary, setSummary] = useState<{teams: number; groups: number; perGroup: number; total: number} | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Carry the entered teams + format into a real ScorePal tournament (via signup) — no retyping.
    const makeItLive = () => {
        const teams = teamsText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (teams.length < 4) { setError("Please enter at least four teams or players."); return; }
        const groupCount = Math.min(16, Math.max(2, parseInt(groups, 10) || 2));
        stashGeneratorTournament({
            source: "group-and-knockout-generator",
            format: "groups_knockout",
            sport: "padel",
            teams,
            groupCount: Math.min(groupCount, teams.length),
            qualifiers: parseInt(qualifiers, 10) === 1 ? 1 : 2,
        });
        navigate({to: "/start/from-generator" as any});
    };

    // Short staged progress before the reveal — the build itself is instant, but flashing a full
    // tournament in the same frame as the click reads as "nothing happened". Each stage names the
    // real step being performed.
    const GEN_STAGES = ["Splitting teams into groups", "Generating round-robin fixtures", "Seeding the knockout bracket"];
    const [genStage, setGenStage] = useState<number | null>(null);

    const generate = () => {
        if (genStage !== null) return; // already running
        const teams = teamsText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (teams.length < 4) {
            setError("Please enter at least four teams or players.");
            setResult(null);
            setSummary(null);
            return;
        }
        const groupCount = Math.min(16, Math.max(2, parseInt(groups, 10) || 2));
        if (groupCount > teams.length) {
            setError("The number of groups cannot exceed the number of teams.");
            setResult(null);
            setSummary(null);
            return;
        }
        const perGroup = parseInt(qualifiers, 10) === 1 ? 1 : 2;
        setError(null);
        setResult(null);
        setSummary(null);
        setGenStage(0);
        GEN_STAGES.forEach((_, i) => setTimeout(() => setGenStage(i), i * 550));
        setTimeout(() => {
            setResult(buildGroupKnockout(teams, groupCount, perGroup));
            setSummary({teams: teams.length, groups: groupCount, perGroup, total: groupCount * perGroup});
            setGenStage(null);
        }, GEN_STAGES.length * 550 + 250);
    };

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Group and Knockout Tournament Generator (Free) | ScorePal</title>
            <meta name="description" content="Create a group and knockout tournament schedule instantly. Generate group stage fixtures, qualifiers and knockout brackets for football, basketball, baseball, cricket, rugby, padel, tennis, esports and club events." />
            <link rel="canonical" href={`${config.publicUrl}/group-and-knockout-generator/`} />
            <meta property="og:title" content="Group and Knockout Tournament Generator | ScorePal" />
            <meta property="og:description" content="Generate group stage fixtures and knockout brackets for tournaments and club events." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/group-and-knockout-generator/`} />
            <meta property="og:image" content="https://scorepal.co/images/group-and-knockout-generator-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Group and Knockout Tournament Generator | ScorePal" />
            <meta name="twitter:description" content="Create group stage fixtures and knockout brackets instantly with ScorePal." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Group and Knockout Tournament Generator",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "A free online tool to generate group stage fixtures and knockout brackets for sports, clubs and competitions.",
                "url": "https://scorepal.co/group-and-knockout-generator/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
                "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is a group and knockout tournament?", "acceptedAnswer": {"@type": "Answer", "text": "A group and knockout tournament starts with teams or players competing in groups, followed by the top qualifiers advancing into a knockout bracket."}},
                    {"@type": "Question", "name": "How many teams qualify from each group?", "acceptedAnswer": {"@type": "Answer", "text": "Most group and knockout tournaments qualify one or two teams from each group, depending on the number of groups and knockout bracket size."}},
                    {"@type": "Question", "name": "What sports use group and knockout formats?", "acceptedAnswer": {"@type": "Answer", "text": "Group and knockout formats are common in football, basketball, baseball, cricket, rugby, padel, tennis, esports, school competitions and club tournaments."}},
                    {"@type": "Question", "name": "How are group fixtures generated?", "acceptedAnswer": {"@type": "Answer", "text": "Group fixtures are usually generated as round robin schedules, where each participant plays the others in their group."}},
                    {"@type": "Question", "name": "Can ScorePal manage group standings and knockout brackets?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal manages fixtures, live scores, standings, qualifiers, knockout brackets and player communication."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Group and Knockout Tournament Generator", item: "https://scorepal.co/group-and-knockout-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free Tournament Generator
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Group and Knockout Tournament Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create group stage fixtures and knockout brackets for football, basketball, baseball, cricket, rugby,
                                    padel, tennis, esports and club tournaments. Split teams into groups, generate matches and advance
                                    qualifiers into a knockout stage.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Compare Formats
                                    </Link>
                                </div>
                            </div>

                            {/* Generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Create Your Tournament</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter teams, choose groups and how many qualify.</p>

                                <label htmlFor="teams" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players or teams</label>
                                <textarea
                                    id="teams"
                                    value={teamsText}
                                    onChange={(e) => setTeamsText(e.target.value)}
                                    placeholder={"Enter one team or player per line\nTeam 1\nTeam 2\nTeam 3\nTeam 4\nTeam 5\nTeam 6\nTeam 7\nTeam 8"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <div className="grid grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <label htmlFor="groups" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Groups</label>
                                        <input id="groups" type="number" min={2} max={16} value={groups} onChange={(e) => setGroups(e.target.value)}
                                               className="w-full rounded-xl border border-[#e5e7eb] p-3 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40" />
                                    </div>
                                    <div>
                                        <label htmlFor="qualifiers" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Qualifiers / group</label>
                                        <select id="qualifiers" value={qualifiers} onChange={(e) => setQualifiers(e.target.value)}
                                                className="w-full rounded-xl border border-[#e5e7eb] p-3 font-inter text-[#1a1a2e] bg-white focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40">
                                            <option value="1">Top 1 qualifies</option>
                                            <option value="2">Top 2 qualify</option>
                                        </select>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={genStage !== null}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {genStage !== null ? "Generating…" : "Generate Group and Knockout"}
                                </button>

                                {/* Staged progress: bar fills as each named step "runs". */}
                                {genStage !== null && (
                                    <div className="mt-4">
                                        <div className="h-2 w-full overflow-hidden rounded-full bg-[#EDEEF4]">
                                            <div className="h-full rounded-full bg-[#082F8A] transition-all duration-500"
                                                 style={{width: `${Math.round(((genStage + 1) / GEN_STAGES.length) * 100)}%`}} />
                                        </div>
                                        <p className="mt-2 animate-pulse font-inter text-sm text-[#1a1a2e]/70">{GEN_STAGES[genStage]}…</p>
                                    </div>
                                )}

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.teams}</strong> participants · <strong>{summary.groups}</strong> groups ·{" "}
                                        <strong>{summary.perGroup}</strong> qualifier{summary.perGroup > 1 ? "s" : ""}/group ·{" "}
                                        <strong>{summary.total}</strong> total qualifiers
                                    </p>
                                )}

                                {/* Once generated, the conversion CTA appears right here at the top —
                                    the full pitch card at the bottom of the results repeats it. */}
                                {result && (
                                    <div className="mt-4 rounded-xl border-2 border-[#082F8A]/20 bg-[#F6F7FB] p-4">
                                        <p className="font-inter text-sm font-semibold text-[#1a1a2e]">Your draw is ready below 👇</p>
                                        <p className="font-inter text-xs text-[#1a1a2e]/60 mt-0.5">Make it live with scores, standings and a shareable page — we'll keep these teams and this format.</p>
                                        <button onClick={makeItLive}
                                                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#082F8A] py-2.5 font-inter font-semibold text-white transition hover:bg-[#06246b]">
                                            Create my live tournament
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results */}
            {result && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                            Your Group and Knockout Tournament
                        </h2>

                        {/* Groups */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {result.groups.map((group) => (
                                <div key={group.letter} className="bg-[#F6F7FB] border border-[#e5e7eb] rounded-2xl p-5">
                                    <div className="font-bebas text-xl text-[#082F8A] font-bold mb-3">Group {group.letter}</div>
                                    <div className="bg-white border border-[#e5e7eb] rounded-xl p-3 mb-4">
                                        {group.teams.map((t, i) => (
                                            <div key={i} className={`flex items-center justify-between gap-2 py-1.5 font-inter text-sm ${i < group.teams.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                                <span className="font-semibold text-[#1a1a2e]">{t.name}</span>
                                                {t.qualifier && <span className="text-[#12b76a] font-semibold text-xs">Projected qualifier</span>}
                                            </div>
                                        ))}
                                    </div>
                                    {group.fixtures.map((roundMatches, ri) => (
                                        <div key={ri} className="mb-3">
                                            <div className="font-inter font-semibold text-[#1a1a2e]/70 text-xs uppercase tracking-wide mb-1.5">Round {ri + 1}</div>
                                            <div className="space-y-1.5">
                                                {roundMatches.map((m, mi) => (
                                                    <div key={mi} className="bg-white border border-[#e5e7eb] rounded-lg px-3 py-2 font-inter text-sm text-[#1a1a2e]">{m}</div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        {/* Knockout */}
                        <div className="mt-10">
                            <h3 className="font-bebas text-2xl text-[#1a1a2e] font-bold mb-1">Knockout Stage</h3>
                            <p className="font-inter text-[#1a1a2e]/70 text-sm mb-5">Qualifiers are seeded into the bracket. Replace placeholders with actual group winners and runners-up after the group stage.</p>
                            <div className="flex gap-6 overflow-x-auto pb-4">
                                {result.knockout.map((round, ri) => (
                                    <div key={ri} className="min-w-[240px] flex flex-col gap-4">
                                        <div className="font-bebas text-lg text-[#082F8A] font-bold">{round.title}</div>
                                        {round.matches.map((m, mi) => (
                                            <div key={mi} className="rounded-2xl border border-[#e5e7eb] overflow-hidden bg-white">
                                                {[m.t1, m.t2].map((name, ti) => (
                                                    <div key={ti} className={`px-4 py-3 font-inter text-sm bg-[#F8FAFC] ${ti === 0 ? "border-b border-[#e5e7eb]" : ""} ${m.placeholder || name === "BYE" ? "text-[#1a1a2e]/40 italic" : "text-[#1a1a2e]"}`}>
                                                        {name}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-10 rounded-2xl border-2 border-[#082F8A]/20 bg-[#F6F7FB] p-6 lg:p-8">
                            <h3 className="font-bebas text-2xl lg:text-3xl text-[#1a1a2e] font-bold tracking-wide">Make it live on ScorePal — free</h3>
                            <p className="font-inter text-sm text-[#1a1a2e]/70 mt-1 max-w-2xl">
                                Turn this into a real tournament with live scores, auto-updating standings, a shareable page and an embeddable widget. We'll keep your teams and this exact group + knockout format — no retyping.
                            </p>
                            <div className="mt-5">
                                <button onClick={makeItLive}
                                        className="inline-flex items-center justify-center gap-2 bg-[#082F8A] text-white rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-[#06246b] transition">
                                    <span className="font-inter text-base font-medium">Create my live tournament</span>
                                    <div className="bg-white rounded-full p-1">
                                        <ArrowUpRightIcon className="text-[#082F8A]" size={28} />
                                    </div>
                                </button>
                            </div>
                            <p className="font-inter text-xs text-[#1a1a2e]/50 mt-3">Free to start — you'll add a few details, then land straight on your live draw.</p>
                        </div>
                    </div>
                </section>
            )}

            {/* What is group + knockout / steps */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is a Group and Knockout Tournament?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        A group and knockout tournament begins with teams or players divided into groups. Each group plays a
                        mini league, then the best performers qualify for a knockout bracket until a champion is decided.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Group+KO vs Round Robin */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Group and Knockout vs Round Robin
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Ideal when you want every participant to play multiple matches but still finish with an exciting final bracket.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">Group + Knockout</div>
                                <div className="p-5 text-[#1a1a2e]">Round Robin</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.g}</div>
                                    <div className="p-5 text-[#1a1a2e]/80">{row.rr}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* When to use */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        When Should You Use a Group and Knockout Format?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#12b76a] mb-3">Best For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {BEST_FOR.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#d92d20] mb-3">Less Ideal For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {LESS_IDEAL.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(30px,5vw,48px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your Group and Knockout Tournament With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Generate groups, manage fixtures, track standings, advance qualifiers and publish live knockout results from one tournament platform.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Group Stage", "Knockout Brackets", "Live Scores", "Standings", "Qualifiers", "Finals"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Group and Knockout FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
